/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FunctionComponent } from "react";

import { useTranslation } from "react-i18next";
import {
  COPY,
  TransformData,
  TransformFieldProps,
  TransformTypeEnum,
} from "@advapacs/common";

import Validator from "../validator";
import HL7FieldInput from "../../Input/HL7FieldInput";
import DicomInput from "../../Input/DicomInput";
import ExternalError from "./ExternalError";

const Copy: FunctionComponent<TransformFieldProps> = ({
  disabled,
  id,
  errors,
  transform,
  onValueChangeHandler,
  transformType,
  isDark = false,
}: TransformFieldProps) => {
  const { t } = useTranslation("form");

  const dispatchNewValue = (trans: TransformData) => {
    onValueChangeHandler({
      type: COPY,
      id,
      transform: trans,
    });
  };

  const updateFrom = async (selected: string) => {
    dispatchNewValue({ ...transform, source: selected });
  };

  const updateTo = async (selected: string) => {
    dispatchNewValue({ ...transform, destination: selected });
  };

  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === COPY) !== undefined
    );
  };

  return (
    <div className="row">
      <div className="col-4 mb-1">
        {transformType === TransformTypeEnum.HL7 && (
          <HL7FieldInput
            onChange={updateFrom}
            placeholder={t("hl7.source_hl7_field")}
            initialValue={transform?.source}
            disabled={disabled}
          />
        )}

        {transformType === TransformTypeEnum.DICOM && (
          <DicomInput
            isDark={isDark}
            placeholder={t("rule:source_tag")}
            onChange={updateFrom}
            initialValue={transform?.source}
            disabled={disabled}
          />
        )}

        {hasError() && !transform?.source && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_source_tag`}
          >
            {t("field_required")}
          </label>
        )}
        {hasError() &&
          transform?.source &&
          !Validator.validateTag(transform?.source, transformType) && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_source_tag`}
            >
              {transformType === TransformTypeEnum.HL7
                ? t("hl7.hl7_field_format_invalid")
                : t("rule:tag_format_invalid")}
            </label>
          )}
        <ExternalError
          errors={errors}
          id={id}
          field="source"
          htmlFor={`${id}_source_tag`}
        />
      </div>
      <div className="col-4 mb-1">
        {transformType === TransformTypeEnum.HL7 && (
          <HL7FieldInput
            onChange={updateTo}
            placeholder={t("hl7.destination_hl7_field")}
            initialValue={transform?.destination}
            disabled={disabled}
          />
        )}
        {transformType === TransformTypeEnum.DICOM && (
          <DicomInput
            isDark={isDark}
            placeholder={t("rule:destination_tag")}
            onChange={updateTo}
            initialValue={transform?.destination}
            disabled={disabled}
          />
        )}
        {hasError() && !transform?.destination && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_destination_tag`}
          >
            {t("field_required")}
          </label>
        )}
        {hasError() &&
          transform?.destination &&
          !Validator.validateTag(transform?.destination, transformType) && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_destination_tag`}
            >
              {transformType === TransformTypeEnum.HL7
                ? t("hl7.hl7_field_format_invalid")
                : t("rule:tag_format_invalid")}
            </label>
          )}
        <ExternalError
          errors={errors}
          id={id}
          field="destination"
          htmlFor={`${id}_destination_tag`}
        />
      </div>
    </div>
  );
};

export default Copy;
